import { createRoute, useNavigate } from '@tanstack/react-router'
import { useContext } from 'react'
import { Compass, ExternalLink, MapPin, Pencil } from 'lucide-react'
import rootRoute from './rootRoute'
import type { ReactNode } from 'react'
import { $api } from '@/lib/fetch'
import { BackLink, Badge, Button, Row } from '@/components/ui'
import { catOf } from '@/lib/types'
import { entityIdFromSlug, safeHref } from '@/lib/helpers'
import { LoginContext } from '@/lib/auth'
import { EventStatus } from '@/lib/constants'
import EventCard from '@/components/EventCard'
import Loader from '@/components/Loader'
import MarkdownContent from '@/components/MarkdownContent'

function Overline({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 14,
        textTransform: 'uppercase',
        letterSpacing: '0.18em',
        fontSize: 12,
        fontWeight: 700,
        color: 'var(--gold-700)',
      }}
    >
      {children}
    </div>
  )
}

function Rule() {
  return (
    <div
      aria-hidden="true"
      style={{
        height: 1,
        background: 'var(--border-soft)',
        margin: '32px 0',
        border: 'none',
      }}
    />
  )
}

function EntityDetailView() {
  const { slug } = entityDetailRoute.useParams()
  const navigate = useNavigate()
  const ctx = useContext(LoginContext)
  const isAdmin = ctx?.user?.admin || false
  const id = entityIdFromSlug(slug)

  const {
    data: entityData,
    isLoading,
    isError,
  } = $api.useQuery('get', '/api/entities/{id}', { params: { path: { id } } })
  const { data: kindsData } = $api.useQuery('get', '/api/entity-kinds')
  const { data: eventsData } = $api.useQuery('post', '/api/events/search', {
    body: {
      entity_id: id,
      status: EventStatus.Verified,
      min_date: new Date().toISOString().slice(0, 10),
      limit: 3,
      order: 'asc',
    },
  })

  if (isLoading) return <Loader />

  if (isError || !entityData?.entity) {
    return (
      <div
        style={{
          maxWidth: 'var(--container-max)',
          margin: '0 auto',
          padding: 'var(--space-8) var(--gutter)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        <p style={{ color: 'var(--danger)', margin: 0 }}>Entité introuvable.</p>
        <Button
          variant="secondary"
          onClick={() => navigate({ to: '/communautes' })}
        >
          ← Retour
        </Button>
      </div>
    )
  }

  const entity = entityData.entity
  const kind = kindsData?.kinds?.find(
    (k: { id: number }) => k.id === entity.kind_id,
  ) as { id: number; name: string; emoji: string } | undefined
  const tags: Array<string> = entity.tags ?? []
  const upcoming = eventsData?.events ?? []

  const mapsHref = entity.location_text
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(entity.location_text)}`
    : null

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-page)' }}>
      {/* Back link + admin edit */}
      <div
        style={{
          maxWidth: 1120,
          margin: '0 auto',
          padding: 'var(--space-4) var(--gutter) 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <BackLink onClick={() => navigate({ to: '/communautes' })}>
          Communautés
        </BackLink>
        {isAdmin && (
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<Pencil size={15} />}
            onClick={() =>
              navigate({
                to: '/admin/entities/$id/edit',
                params: { id: String(entity.id) },
              })
            }
          >
            Modifier
          </Button>
        )}
      </div>

      {/* Hero */}
      <section
        style={{
          position: 'relative',
          background:
            'linear-gradient(135deg, var(--emerald-700) 0%, var(--emerald-900) 100%)',
          overflow: 'hidden',
          marginTop: 'var(--space-4)',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: "url('/assets/zellij-tile.svg')",
            backgroundSize: '120px 120px',
            opacity: 0.14,
            mixBlendMode: 'soft-light',
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 2,
            background: 'var(--gold-500)',
            opacity: 0.85,
          }}
        />
        <div
          style={{
            position: 'relative',
            maxWidth: 1120,
            margin: '0 auto',
            padding: '44px var(--gutter)',
            display: 'flex',
            alignItems: 'center',
            gap: 30,
            flexWrap: 'wrap',
          }}
        >
          {/* Logo tile */}
          <div
            style={{
              flexShrink: 0,
              width: 168,
              height: 128,
              background: '#fff',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--gold-400)',
              boxShadow: 'var(--shadow-lg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 18,
            }}
          >
            {entity.image_uri ? (
              <img
                src={entity.image_uri}
                alt={entity.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <span style={{ fontSize: 56, lineHeight: 1 }}>{kind?.emoji}</span>
            )}
          </div>

          {/* Info */}
          <div style={{ minWidth: 260, flex: 1 }}>
            {kind && (
              <div style={{ marginBottom: 14 }}>
                <Badge tone="gold" solid>
                  {kind.emoji} {kind.name}
                </Badge>
              </div>
            )}
            <h1
              style={{
                margin: 0,
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(34px, 4.6vw, 52px)',
                lineHeight: 1.08,
                color: 'var(--ink-on-dark)',
                letterSpacing: '-0.01em',
              }}
            >
              {entity.name}
            </h1>
            {entity.location_text && (
              <div
                style={{
                  marginTop: 12,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  color: 'var(--gold-200)',
                  fontSize: 15.5,
                  fontWeight: 500,
                }}
              >
                <MapPin size={17} />
                {entity.location_text}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Body */}
      <main
        className="oumma-detail-grid"
        style={{
          maxWidth: 1120,
          margin: '0 auto',
          padding: '48px var(--gutter) 80px',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 340px',
          gap: 48,
          alignItems: 'start',
        }}
      >
        {/* Main column */}
        <div>
          <Overline>À propos</Overline>
          {entity.description && (
            <MarkdownContent>{entity.description}</MarkdownContent>
          )}
          {(() => {
            const href = safeHref(entity.link)
            if (!href) return null
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  marginTop: 16,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  color: 'var(--emerald-700)',
                  fontWeight: 600,
                  fontSize: 15.5,
                  textDecoration: 'none',
                }}
              >
                {(() => {
                  try {
                    return new URL(href).hostname.replace(/^www\./, '')
                  } catch {
                    return href
                  }
                })()}
                <ExternalLink size={15} />
              </a>
            )
          })()}

          {tags.length > 0 && (
            <>
              <Rule />
              <Overline>Activités proposées</Overline>
              <Row gap="sm" wrap>
                {tags.map((tag) => (
                  <Badge key={tag} tone={catOf(tag).tone}>
                    {tag}
                  </Badge>
                ))}
              </Row>
            </>
          )}

          <Rule />

          <h2
            style={{
              margin: '0 0 18px',
              fontFamily: 'var(--font-display)',
              fontSize: 30,
              lineHeight: 1.15,
              color: 'var(--ink-0)',
            }}
          >
            Prochains événements
          </h2>
          {upcoming.length === 0 ? (
            <p
              style={{
                color: 'var(--ink-3)',
                fontStyle: 'italic',
                fontSize: 15,
                margin: 0,
              }}
            >
              Aucun événement à venir pour le moment.
            </p>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 'var(--space-5)',
              }}
            >
              {upcoming.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside
          className="oumma-detail-side"
          style={{ position: 'sticky', top: 88 }}
        >
          <div
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-soft)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-sm)',
              padding: 24,
            }}
          >
            {entity.location_text && (
              <>
                <Overline>Adresse</Overline>
                <div
                  style={{
                    display: 'flex',
                    gap: 11,
                    marginBottom: 20,
                  }}
                >
                  <MapPin
                    size={20}
                    color="var(--gold-600)"
                    style={{ flexShrink: 0, marginTop: 2 }}
                  />
                  <span
                    style={{
                      fontSize: 16,
                      lineHeight: 1.5,
                      color: 'var(--ink-1)',
                    }}
                  >
                    {entity.location_text}
                  </span>
                </div>
              </>
            )}

            {safeHref(entity.link) && (
              <a
                href={safeHref(entity.link)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '10px 18px',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--brand-primary)',
                  color: 'var(--paper-0)',
                  border: '1px solid var(--brand-primary)',
                  boxShadow: 'var(--shadow-sm)',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 600,
                  fontSize: 15,
                  textDecoration: 'none',
                  marginBottom: 10,
                  boxSizing: 'border-box',
                }}
              >
                <ExternalLink size={17} />
                Voir la page
              </a>
            )}

            {mapsHref && (
              <a
                href={mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '10px 18px',
                  borderRadius: 'var(--radius-md)',
                  background: 'transparent',
                  color: 'var(--ink-1)',
                  border: '1px solid transparent',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 600,
                  fontSize: 15,
                  textDecoration: 'none',
                  boxSizing: 'border-box',
                }}
              >
                <Compass size={17} />
                Obtenir l'itinéraire
              </a>
            )}

            {!entity.location_text && !entity.link && (
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  color: 'var(--ink-3)',
                  fontStyle: 'italic',
                }}
              >
                Informations de contact à venir.
              </p>
            )}
          </div>
        </aside>
      </main>
    </div>
  )
}

export const entityDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/communautes/$slug',
}).lazy(() => import('./EntityDetailPage.lazy').then((m) => m.Route))

export default EntityDetailView
